import React, { useState } from 'react';
import { useDispatch } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Divider,
  IconButton,
  InputAdornment,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { login } from "../store/auth";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CloseIcon from '@mui/icons-material/Close';
import art from '../assets/art.jpg';
import { useNavigate } from 'react-router-dom';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

const LoginModal = ({ onClose, handleSignup }) => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [forgotOpen, setForgotOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
    setEmail('');
    setPassword('');
    onClose();
  };

  const handleLogin = async () => {
    if (!email) {
      toast.error("Email is required", { theme: "dark" });
      return;
    }

    const body = {
      email: email,
      password: 'any-password-works'
    };

    setLoading(true);
    try {
      const result = await dispatch(login(body));
      
      if (result.meta.requestStatus === "fulfilled") {
        toast.success("Login successful!", { theme: "dark" });
        handleClose();
      } else {
        const errorMessage = result.payload?.message || "Login failed. Please try again.";
        toast.error(errorMessage, { theme: "dark" });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "An error occurred during login", { theme: "dark" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Dialog
        open={open}
        onClose={handleClose}
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#3c3c3c',
            color: '#A68E5D',
            borderRadius: 2,
            maxWidth: '400px',
            width: '100%',
            padding: '20px',
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.3)',
            textAlign: 'center',
          },
          '& .MuiBackdrop-root': {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', top: 10, right: 10, color: '#A68E5D' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle
          sx={{
            color: '#A68E5D',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            paddingBottom: 0,
            fontSize: '1.5rem',
            textAlign: 'center',
          }}
        >
          <img src={art} alt="Icon" style={{ width: '50px', marginBottom: '10px' }} />
          Log in to your account
        </DialogTitle>
        <Divider
          sx={{
            mt: 2,
            mb: 2,
            backgroundColor: 'white',
            height: '1px',
            width: '100%',
          }}
        />
        <DialogContent>
          <TextField
            label="Email"
            variant="outlined"
            fullWidth
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            InputLabelProps={{
              style: { color: 'white' },
            }}
            InputProps={{
              style: { backgroundColor: 'transparent', color: 'white' },
            }}
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'white',
                },
                '&:hover fieldset': {
                  borderColor: 'white',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },
              '& .MuiInputLabel-outlined.Mui-focused': {
                color: 'white',
              },
            }}
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            fullWidth
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            InputLabelProps={{
              style: { color: 'white' },
            }}
            InputProps={{
              style: { backgroundColor: 'transparent', color: 'white' },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    sx={{ color: 'white' }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderColor: 'white',
                },
                '&:hover fieldset': {
                  borderColor: 'white',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'white',
                },
              },
              '& .MuiInputLabel-outlined.Mui-focused': {
                color: 'white',
              },
            }}
          />

          <Button
            variant="contained"
            fullWidth
            onClick={handleLogin}
            disabled={loading || !email}
            sx={{
              backgroundColor: '#A68E5D',
              color: 'white',
              mt: 2,
              padding: '10px',
              fontSize: '1rem',
              '&:hover': {
                backgroundColor: '#8a7449',
              },
              '&:disabled': {
                backgroundColor: 'rgba(166, 142, 93, 0.5)',
              }
            }}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </Button>

          <Typography
            variant="body2"
            sx={{ mt: 2, color: '#A68E5D', cursor: 'pointer', textDecoration: 'underline' }}
            // onClick={() => setForgotOpen(true)}
          >
            Forgot Password?
          </Typography>

          <Typography
            variant="body2"
            sx={{ mt: 3, color: 'white', fontSize: '0.8rem' }}
          >
            New around here?{' '}
            <Link
              onClick={handleSignup}
              sx={{ color: '#A68E5D', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Create an account
            </Link>
          </Typography>
        </DialogContent>
      </Dialog>
      <ForgotPasswordModal open={forgotOpen} onClose={() => setForgotOpen(false)} />
      <ToastContainer />
    </Box>
  );
};

export default LoginModal;